import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { userApi } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import SyntaxHighlighter from '../components/SyntaxHighlighter'

const AnalysisPage: React.FC = () => {
  const { logout } = useAuth()

  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['latestResult'],
    queryFn: () => userApi.getResults(1, 1),
    refetchInterval: 2000, // Poll for real-time updates
  })

  const latestResult = resultsData?.results?.[0]

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-serif">
      {/* Minimalist Header */}
      <header className="absolute top-0 right-0 p-4 flex items-center space-x-4">
        <Link to="/dashboard" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">
          &larr; Back to Dashboard
        </Link>
        <button onClick={logout} className="text-sm text-gray-500 hover:text-blue-600 transition-colors">
          Sign Out
        </button>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 w-full">
        {!latestResult ? (
          <div className="text-center py-20">
            <h2 className="text-2xl font-display font-bold">No Analysis Available</h2>
            <p className="mt-2 text-gray-600">
              Once you submit a screenshot, the latest analysis will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="prose max-w-none">
            <ReactMarkdown
              children={latestResult.llm_output_text}
              remarkPlugins={[remarkGfm]}
              components={{
                code(props) {
                  const { children, className } = props
                  const match = /language-(\w+)/.exec(className || '')
                  return match ? (
                    <SyntaxHighlighter language={match[1]}>
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className}>{children}</code>
                  )
                },
              }}
            />
          </div>
        )}
      </main>
    </div>
  )
}

export default AnalysisPage